import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ANALYSIS_IDLE, ANALYSIS_STEPS, nameAndTown, ONLINE_ADVICE, ONLINE_KINDS, ONLINE_TOOLS, PROFILE_ESSENTIAL,
  PROFILE_SECTIONS, profileAsked, profileMarkdown, WANT_MORE,
  type AnalysisState, type BrandChoice, type OnlineTool, type ProfileSection, type ProfileSource,
  type ProfileSectionSpec, type ProfileResponse, type SeoResponse, type SetupResponse, type Suggestions, type Voice,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { AskForReviews } from "./AskForReviews.js";
import { Icon } from "../ui/icons.js";
import { useProfile } from "../ui/reads.js";
import { useNarrow } from "../ui/narrow.js";
import { photoGroup } from "../ui/photos.js";
import { NotBuilt, SideMenu } from "./chrome.js";
import { LookPanel } from "./LookPanel.js";
import { PhotosPanel } from "./PhotosPanel.js";
import { MailboxPanel } from "./MailboxPanel.js";
import { ChannelsPanel } from "./ChannelsPanel.js";
import { Connections } from "./Connections.js";
import { ConnectSite, WriterSettings } from "./SeoSite.js";
import { WebsitePanel } from "./WebsitePanel.js";
import { Disconnect, GoogleSays } from "./SeoGoogle.js";
import { Markets } from "../customers/setup/Groups.js";
import { FirstDraft } from "../customers/setup/FirstDraft.js";

/** Sections only a lookup writes: from Google's reviews, and from the businesses it found nearby. */
const LOOKUP_ONLY: readonly string[] = ["loved", "rivals"];
/** What the Studio writes every brief from, on the left of *About the business*; what the lookup found is on the right. */
const WRITES_FROM: readonly string[] = ["what", "who", "tone", "works"];

const NO_LOOK = { logoUrl: null, palette: [], logoColours: [], siteColours: [], endLine: "" };

const SOURCE: Record<ProfileSource, string> = {
  you: "yours", places: "from Google", website: "from your website", guessed: "our guess",
};

/**
 * Business is a menu of screens down the left (03 §business context, ADR 0049 as amended the same day). The owner
 * asked for no scrolling, columns to show more, and the set-up in the same screens so nobody is lost between them.
 *
 * So the menu is there from the first second, and **during set-up it is the checklist**: each item waits, fills and
 * ticks, and *Next* walks down it. Afterwards the same items are how you get anywhere. Analytics, Customers and SEO
 * are asked here as well as on their own tabs — the same rows either way — and SEO waits until there is a website.
 */
export type BusinessView = "where" | "about" | "look" | "photos" | "site" | "website" | "channels" | "customers" | "seo" | "reviews";
const MENU: ReadonlyArray<{ key: BusinessView; label: string; hint: string }> = [
  { key: "where", label: "Where to find you", hint: "name, town, website" },
  { key: "about", label: "About the business", hint: "what the Studio writes from" },
  { key: "look", label: "Your look", hint: "logo and colours" },
  { key: "photos", label: "Photos", hint: "what posts are made with" },
  { key: "site", label: "Website & analytics", hint: "who visits, from where" },
  // Only for a business with no website of its own (28): drawn in the menu then, and gone once there is one.
  { key: "website", label: "Your website", hint: "one page, made for you" },
  { key: "channels", label: "Channels", hint: "where posts go out" },
  { key: "customers", label: "Customers", hint: "who to write to, from your mailbox" },
  { key: "seo", label: "SEO", hint: "articles on your own site" },
  { key: "reviews", label: "Ask for reviews", hint: "a link and a QR code" },
];
type Status = "done" | "reading" | "waiting" | "todo" | "locked";

/** A step named in the address (`/business/channels`), or null for anything that is not one of them. */
export const businessViewOf = (key: string | undefined): BusinessView | null => MENU.find((m) => m.key === key)?.key ?? null;

/** Steps waved past with *Skip for now*. Per browser: a skip is a way through the set-up, not a setting. */
const SKIPPED = "joogoos.business.skipped";
function readSkipped(): BusinessView[] {
  try { return JSON.parse(localStorage.getItem(SKIPPED) ?? "[]") as BusinessView[]; } catch { return []; }
}
function writeSkipped(keys: BusinessView[]) {
  try { localStorage.setItem(SKIPPED, JSON.stringify(keys)); } catch { /* a private window forgets: the step comes back, which is fine */ }
}

/**
 * Business (03 §business context): who this company is, written down once, in front of every brief afterwards — and
 * everything it is plugged into. The document is the deliverable, not a summary of one: it is the markdown that goes
 * to the model with every brief, and the customer can read the exact words.
 */
export function Business({ step, onStep }: { step?: BusinessView | null; onStep?: (k: BusinessView) => void } = {}) {
  const qc = useQueryClient();
  const q = useProfile();
  const narrow = useNarrow();
  /**
   * The step somebody chose, or null to land by the rule below. The shell keeps it, in the address with the tab
   * (`/business/channels`), so a reload, a pasted link and a return from a platform's sign-in all open the same one;
   * drawn on its own, as the tests draw it, the page keeps it itself.
   */
  const [own, setOwn] = useState<BusinessView | null>(null);
  const picked = onStep ? step ?? null : own;
  const [skipped, setSkipped] = useState<BusinessView[]>(readSkipped);
  // The same keys the panels read with, so the menu's ticks cost no second request.
  const site = useQuery({ queryKey: ["site", 30, null, null], queryFn: () => api.site(30) });
  const channels = useQuery({ queryKey: ["channels"], queryFn: () => api.channels() });
  const setup = useQuery({ queryKey: ["customers", "setup"], queryFn: () => api.customersSetup() });
  const seo = useQuery({ queryKey: ["seo"], queryFn: () => api.seo() });
  const media = useQuery({ queryKey: ["media", "image"], queryFn: () => api.media("image") });
  const built = useQuery({ queryKey: ["website"], queryFn: () => api.website() });
  if (q.isPending) return null;
  // A read that failed is said, not dereferenced: with no open workspace (ADR 0022) this answer is a refusal, and it
  // arrived a moment before the shell's own — long enough to take the whole page down with it (2026-09-24).
  if (!q.data) return <div className="h-full grid place-items-center text-faint text-sm">{q.error?.message ?? "The business page could not be read."}</div>;
  const data = q.data;
  const profile = data.profile;
  const analysis = data.analysis ?? ANALYSIS_IDLE;
  const known = profile.sections.filter((s) => s.body.trim());
  const reading = analysis.status === "running";
  const aboutKeys = aboutAsked(data).map((s) => s.key as string);
  const aboutDone = !reading && !analysis.nearMiss
    && PROFILE_ESSENTIAL.every((k) => known.some((s) => s.key === k))
    && known.every((s) => s.source === "you" || !aboutKeys.includes(s.key));
  const website = site.data?.domain ?? null;
  // A website of the business's own, not one Joogoos made: then *Your website* has nothing to do and leaves the menu.
  const menu = built.data?.otherWebsite ? MENU.filter((m) => m.key !== "website") : MENU;
  const status: Record<BusinessView, Status> = {
    where: known.some((s) => s.key === "name" || s.key === "links") ? "done" : "todo",
    about: reading ? "reading" : aboutDone ? "done" : "todo",
    look: data.brand ? "waiting" : data.look?.logoUrl || data.look?.palette.length ? "done" : "todo",
    // Only what Photos itself shows: a logo is media too, and counting it ticked Photos on a business with none.
    photos: media.data?.some((m) => photoGroup(m) !== null) ? "done" : "todo",
    site: website ? "done" : "todo",
    website: built.data?.status === "live" || built.data?.otherWebsite ? "done" : "todo",
    channels: channels.data?.channels.some((c) => c.status === "connected") ? "done" : "todo",
    customers: setup.data?.signsAs && setup.data.markets.length ? "done" : "todo",
    seo: !website ? "locked" : seo.data?.search.audience.trim() ? "done" : "todo",
    // Never in the way of setting up: it is a tool to pick up, and without a listing there is nothing to pick up.
    reviews: data.reviewLink ? "done" : "locked",
  };
  const settled = (k: BusinessView) => status[k] === "done" || status[k] === "locked" || skipped.includes(k);
  const settingUp = menu.some((m) => !settled(m.key));
  // Where to land: what is being read, then the first thing not settled, then the page itself.
  const current: BusinessView = picked ?? (reading ? "about" : menu.find((m) => !settled(m.key))?.key ?? "about");
  const next = menu.slice(menu.findIndex((m) => m.key === current) + 1).find((m) => !settled(m.key));
  const go = onStep ?? setOwn;
  const skip = () => {
    const all = [...new Set([...skipped, current])];
    setSkipped(all);
    writeSkipped(all);
    if (next) go(next.key);
  };

  const screen = (() => {
    switch (current) {
      case "where": return <Where data={data} onStarted={() => go("about")} />;
      case "about": return <About data={data} />;
      case "look": return <Look data={data} />;
      case "photos": return <Screen title="Photos" sub="Every picture a post can be made with, grouped by where it came from."><PhotosPanel /></Screen>;
      case "site": return <Site website={website ?? site.data?.known ?? null} onMake={() => go("website")} />;
      case "website": return <Screen title="Your website" sub="One page about your business, written by Joogoos and hosted free in your own Cloudflare account."><WebsitePanel /></Screen>;
      case "channels": return <Screen title="Channels" sub="The accounts a finished post goes out on, at the time you choose."><ChannelsPanel /></Screen>;
      case "customers": return <Customers setup={setup.data} keep={(d) => qc.setQueryData(["customers", "setup"], d)} />;
      case "reviews": return <Screen title="Ask for reviews" sub="One link for all your customers, to your own Google review form."><AskForReviews link={data.reviewLink} name={known.find((s) => s.key === "name")?.body.split("\n")[0] ?? ""} /></Screen>;
      case "seo": return <Seo seo={seo.data} website={website} onChanged={() => void qc.invalidateQueries({ queryKey: ["seo"] })} onSite={() => go("site")} />;
    }
  })();

  return (
    <div className={narrow ? "h-full flex flex-col min-h-0" : "h-full flex min-h-0"}>
      <Menu menu={menu} current={current} status={status} skipped={skipped} onPick={go} narrow={narrow} data={data} />
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <div className="max-w-[1180px] min-h-full px-4 md:px-7 py-5 flex flex-col gap-4">
          {!data.available ? <NotBuilt what="business profile" note={data.note} /> : null}
          {screen}
          {settingUp && current !== "where" ? (
            <div className="mt-auto pt-3 border-t border-line flex flex-wrap items-center gap-2.5">
              <span className="text-[12px] text-muted flex-1 min-w-[180px]">Anything you skip waits here, and can be done later.</span>
              {status[current] !== "done" && status[current] !== "reading" && status[current] !== "locked" ? (
                <button onClick={skip} className="px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-[13px] font-medium">Skip for now</button>
              ) : null}
              {next ? (
                <button onClick={() => go(next.key)} className="px-4 py-2 rounded-lg bg-coral text-white text-[13px] font-semibold">Next: {next.label.toLowerCase()} ›</button>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/** The menu down the left, with where each step stands; along the top on a phone. The shape is `SideMenu`'s, which every tab with screens shares. */
function Menu({ menu, current, status, skipped, onPick, narrow, data }: {
  menu: typeof MENU; current: BusinessView; status: Record<BusinessView, Status>; skipped: BusinessView[]; onPick: (k: BusinessView) => void; narrow: boolean; data: ProfileResponse;
}) {
  const done = menu.filter((m) => status[m.key] === "done").length;
  const { name, town } = nameAndTown(data.profile.sections.find((s) => s.key === "name")?.body ?? "");
  const mark = (s: Status) => s === "done" ? <span className="w-[18px] h-[18px] rounded-full bg-good text-white grid place-items-center shrink-0"><Icon name="check" size={11} /></span>
    : s === "reading" || s === "waiting" ? <span className="w-[18px] h-[18px] grid place-items-center shrink-0"><span className="w-2 h-2 rounded-full bg-coral animate-pulse" /></span>
    : s === "locked" ? <span className="w-[18px] h-[18px] grid place-items-center shrink-0 text-faint"><Icon name="lock" size={12} /></span>
    : <span className="w-[18px] h-[18px] rounded-full border-[1.5px] border-line-2 shrink-0" />;
  const hint = (m: (typeof menu)[number]) => status[m.key] === "reading" ? "reading…" : status[m.key] === "waiting" ? "a choice is waiting"
    : status[m.key] === "locked" ? (m.key === "reviews" ? "needs a Google listing" : "needs a website") : skipped.includes(m.key) && status[m.key] === "todo" ? "skipped for now" : m.hint;
  return (
    <SideMenu label="Business" current={current} onPick={onPick} narrow={narrow}
      items={menu.map((m) => ({ ...m, hint: hint(m), mark: mark(status[m.key]), dim: status[m.key] === "locked" }))}
      head={<>
        {data.look?.logoUrl
          ? <img src={data.look.logoUrl} alt="" className="w-10 h-10 rounded-[10px] object-contain bg-panel border border-line" />
          : <span className="w-10 h-10 rounded-[10px] grid place-items-center bg-ink text-white font-extrabold">{initials(name)}</span>}
        <span className="min-w-0 flex flex-col">
          <span className="text-[14px] font-extrabold truncate">{name || "Your business"}</span>
          <span className="text-[11.5px] text-muted truncate">{town || (name ? "" : "not set up yet")}</span>
        </span>
      </>}
      foot={<>
        <div className="flex justify-between text-[11px]"><span className="uppercase tracking-[.08em] font-semibold text-muted">Set up</span><span className="text-muted tabular-nums">{done} of {menu.length}</span></div>
        <div className="h-[5px] rounded-full bg-line overflow-hidden"><div className="h-full bg-coral rounded-full" style={{ width: `${Math.round((done / menu.length) * 100)}%` }} /></div>
      </>}
    />
  );
}

const initials = (name: string) => name.split(/\s+/).filter((w) => /\w/.test(w)).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";

/** A screen's heading, and what it holds under it. */
function Screen({ title, sub, right, children }: { title: string; sub: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <h1 className="font-display text-[22px] font-bold tracking-[-.02em]">{title}</h1>
          <p className="text-[13px] text-muted">{sub}</p>
        </div>
        {right ? <div className="ml-auto flex items-center gap-2">{right}</div> : null}
      </div>
      {children}
    </>
  );
}

/** Two columns on a desk, one on a phone. */
function Columns({ left, right, wide = "left" }: { left: React.ReactNode; right: React.ReactNode; wide?: "left" | "even" }) {
  return (
    <div className={`grid gap-4 items-start ${wide === "left" ? "lg:grid-cols-[1.2fr_1fr]" : "lg:grid-cols-2"}`}>
      <div className="flex flex-col gap-4 min-w-0">{left}</div>
      <div className="flex flex-col gap-4 min-w-0">{right}</div>
    </div>
  );
}

function Panel({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex flex-col gap-2.5" aria-label={title}>
      <div className="flex flex-wrap items-baseline gap-2"><h2 className="text-[14px] font-bold">{title}</h2>{aside ? <span className="ml-auto text-[11.5px] text-muted">{aside}</span> : null}</div>
      {children}
    </section>
  );
}

/**
 * Where to find you (ADR 0049): one field that takes whatever the owner has — a name and a town, a website, an
 * Instagram — and nobody is asked whether they have a website. *It's an idea* takes a sentence instead. Beside it,
 * what the rest of the menu will do. Once named, the same screen holds the name, and changing it reads again.
 */
function Where({ data, onStarted }: { data: ProfileResponse; onStarted: () => void }) {
  const qc = useQueryClient();
  const [idea, setIdea] = useState(false);
  const [text, setText] = useState("");
  const stored = data.available;
  const start = useMutation({
    mutationFn: () => api.startBusiness(text.trim(), idea),
    onSuccess: (r) => { qc.setQueryData(["profile"], r); onStarted(); },
  });
  const go = () => { if (text.trim() && stored && !start.isPending) start.mutate(); };
  const named = data.profile.sections.find((s) => s.key === "name" && s.body.trim());
  const nameSpec = PROFILE_SECTIONS.find((s) => s.key === "name")!;
  const field = named ? (
    <Panel title="Where to find you" aside="changing the name reads the business again">
      <div className="-mx-4 -my-1"><Line spec={nameSpec} section={named} onSaved={(r) => qc.setQueryData(["profile"], r)} /></div>
      {data.profile.stage === "idea" ? <Opened onSaved={(r) => qc.setQueryData(["profile"], r)} /> : null}
    </Panel>
  ) : (
    <Panel title={idea ? "Tell us about the idea" : "Where to find you"} aside="about a minute">
      <div className={`flex gap-2 ${idea ? "flex-col" : "flex-col sm:flex-row"}`}>
        {idea ? (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus disabled={!stored}
            aria-label="The idea" placeholder="e.g. A dog grooming van around Leeds, opening in the spring"
            className="w-full resize-none border border-line-2 rounded-xl px-3.5 py-3 text-[15px] bg-panel outline-none focus:border-ink/30" />
        ) : (
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            autoFocus disabled={!stored} aria-label="Where to find your business" placeholder="e.g. Bramley Bakehouse, Ledbury"
            className="flex-1 min-w-0 border border-line-2 rounded-xl px-3.5 py-3 text-[15px] bg-panel outline-none focus:border-ink/30" />
        )}
        <button onClick={go} disabled={!text.trim() || !stored || start.isPending}
          className="self-start sm:self-stretch px-6 py-3 rounded-xl bg-coral text-white text-[15px] font-semibold disabled:opacity-40">
          {start.isPending ? "Starting…" : idea ? "Draft my page" : "Read it"}
        </button>
      </div>
      {idea ? null : <p className="text-[12px] text-muted">A name and town · a website · an Instagram or Facebook page · nothing online yet? just the name and town</p>}
      <p className="text-[12px] text-muted">The lookup is charged per call, like everything else; nothing is posted or sent.</p>
      {start.error ? <p className="text-bad text-xs">{start.error.message}</p> : null}
      <div className="border-t border-line pt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {idea ? (
          <button onClick={() => setIdea(false)} className="text-[13px] text-coral font-semibold">‹ It is already open</button>
        ) : (
          <>
            <span className="text-[13px] font-semibold">Not open yet?</span>
            <span className="text-[12.5px] text-muted">Describe the idea in a sentence or two instead.</span>
            <button onClick={() => setIdea(true)} className="text-[13px] text-coral font-semibold">It's an idea ›</button>
          </>
        )}
      </div>
    </Panel>
  );
  const after = MENU.slice(1).map((m, i) => (
    <li key={m.key} className="flex gap-3 py-2 border-b border-line last:border-0">
      <span className="text-[12px] text-muted tabular-nums w-4">{i + 2}</span>
      <span className="flex flex-col"><span className="text-[13px] font-semibold">{m.label}</span><span className="text-[12px] text-muted">{m.hint}</span></span>
    </li>
  ));
  return (
    <Screen title={named ? "Where to find you" : idea ? "Tell us about the idea." : "Where can we find your business?"}
      sub="Whatever you have. We read it, draft everything on the left, and you check each step.">
      <Columns left={field} right={<Panel title="What happens next" aside="every step can be skipped and come back to"><ol>{after}</ol></Panel>} />
    </Screen>
  );
}

/** The lines *About the business* asks about: what only a lookup can write is shown only when it wrote it. */
function aboutAsked(data: ProfileResponse): ProfileSectionSpec[] {
  const known = data.profile.sections.filter((s) => s.body.trim());
  return profileAsked(data.profile).filter((spec) => spec.key !== "name" && (!LOOKUP_ONLY.includes(spec.key) || known.some((s) => s.key === spec.key)));
}

/**
 * About the business: the same screen while the lookup reads and after (ADR 0049). On the left, what the Studio
 * writes from, filling in; on the right, the lookup's steps and *what do you want more of?* while it runs, then what
 * it found, each line Right or Edit.
 */
function About({ data }: { data: ProfileResponse }) {
  const qc = useQueryClient();
  const set = (r: ProfileResponse) => qc.setQueryData(["profile"], r);
  const confirm = useMutation({ mutationFn: () => api.confirmAll(), onSuccess: set });
  const listing = useMutation({ mutationFn: (ours: boolean) => api.answerListing(ours), onSuccess: set });
  const [raw, setRaw] = useState(false);
  const [online, setOnline] = useState(false);
  const { profile, analysis } = data;
  const reading = analysis.status === "running";
  const known = profile.sections.filter((s) => s.body.trim());
  const asked = aboutAsked(data);
  const left = asked.filter((s) => WRITES_FROM.includes(s.key));
  const right = asked.filter((s) => !WRITES_FROM.includes(s.key));
  const nowhere = analysis.status !== "idle" && !reading && !data.googleListing && !known.some((s) => s.source === "places" || s.source === "website");
  const { name } = nameAndTown(profile.sections.find((s) => s.key === "name")?.body ?? "");
  const miss = analysis.findings.place?.detail;
  const line = (spec: ProfileSectionSpec) => reading && !known.some((s) => s.key === spec.key)
    ? <Filling key={spec.key} spec={spec} />
    : <CheckLine key={spec.key} spec={spec} section={known.find((s) => s.key === spec.key)} suggest={analysis.suggest} onSaved={set} />;

  const lookupSide = reading ? (
    <>
      <Panel title={`Reading ${name || "your business"}`} aside="about a minute"><RunSteps analysis={analysis} /></Panel>
      <WantMore data={data} />
    </>
  ) : (
    <>
      {analysis.nearMiss ? (
        <div className="rounded-[10px] border border-coral/30 bg-panel p-3.5 flex flex-wrap items-center gap-3">
          <p className="text-[13px] leading-snug flex-1 min-w-[220px]">
            <span className="font-semibold">Is this you on Google?</span> {miss ? `${miss.charAt(0).toUpperCase()}${miss.slice(1)}.` : "We found a listing that may be yours."}
          </p>
          <button onClick={() => listing.mutate(true)} disabled={listing.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">Yes, that's us</button>
          <button onClick={() => listing.mutate(false)} disabled={listing.isPending} className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium">No</button>
          {listing.error ? <p className="w-full text-bad text-xs">{listing.error.message}</p> : null}
        </div>
      ) : null}
      {right.length ? (
        <Panel title="What the lookup found" aside={known.some((s) => s.source !== "you" && s.key !== "name") ? (
          <button onClick={() => confirm.mutate()} disabled={confirm.isPending} title="Every line from Google, your website or a guess becomes yours, words unchanged" className="text-coral font-semibold">
            All the rest is right
          </button>
        ) : undefined}>
          <div className="-mx-4 -my-1 divide-y divide-line">{right.map(line)}</div>
          {confirm.error ? <p className="text-bad text-xs">{confirm.error.message}</p> : null}
        </Panel>
      ) : null}
      {nowhere && profile.stage !== "idea" ? (
        <div className="rounded-[10px] border border-line bg-panel p-3.5 flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <Icon name="globe" size={15} />
            <span className="text-[12.5px] leading-snug flex-1 min-w-[200px]">We couldn't find {name || "you"} online. That's fine: your posts work without a website. When you want a page people can check, here are the free ways.</span>
            <button onClick={() => setOnline(!online)} className="text-[12px] text-coral font-semibold">{online ? "Hide" : "Show me"}</button>
          </div>
          {online ? <OnlineTools /> : null}
        </div>
      ) : null}
    </>
  );

  return (
    <Screen title="About the business" sub="What every post and video is written from."
      right={<button onClick={() => setRaw(!raw)} className="text-[12px] text-muted underline underline-offset-2">{raw ? "Back to the page" : "See exactly what the Studio reads"}</button>}>
      {analysis.status === "failed" ? <Stopped data={data} onSaved={set} /> : null}
      {profile.stage === "idea" ? <p className="text-[12.5px] text-muted">Not open yet — still an idea.</p> : null}
      {raw ? (
        <pre className="bg-panel border border-line rounded-[10px] p-4 text-[12px] leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto">{profileMarkdown(profile)}</pre>
      ) : (
        <Columns
          left={<Panel title="What the Studio writes from" aside={reading ? "filling in as we read" : undefined}><div className="-mx-4 -my-1 divide-y divide-line">{left.map(line)}</div></Panel>}
          right={lookupSide}
        />
      )}
    </Screen>
  );
}

/**
 * A lookup that stopped — no credit, a cap reached — says so where the page would have filled, with the one thing to
 * do, rather than leaving empty lines under steps that read as if nothing was found (2026-09-24).
 */
function Stopped({ data, onSaved }: { data: ProfileResponse; onSaved: (r: ProfileResponse) => void }) {
  const name = data.profile.sections.find((s) => s.key === "name")?.body ?? "";
  const again = useMutation({ mutationFn: () => api.tellUs("name", name), onSuccess: onSaved });
  return (
    <div role="alert" className="rounded-[10px] border border-coral/40 bg-panel p-3.5 flex flex-wrap items-center gap-3">
      <p className="text-[13px] leading-snug flex-1 min-w-[240px]">
        <span className="font-semibold">The lookup stopped.</span> {data.analysis.error ?? "Nothing was found, because nothing was asked."}
      </p>
      {name ? (
        <button onClick={() => again.mutate()} disabled={again.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          {again.isPending ? "Starting…" : "Read it again"}
        </button>
      ) : null}
      {again.error ? <p className="w-full text-bad text-xs">{again.error.message}</p> : null}
    </div>
  );
}

/** A line the lookup has not reached yet, drawn as a line to come rather than a blank. */
function Filling({ spec }: { spec: ProfileSectionSpec }) {
  return (
    <div className="px-4 py-3 flex flex-col gap-2" aria-busy="true">
      <Heading spec={spec} />
      <span className="h-2.5 w-4/5 rounded-full bg-line animate-pulse" />
      <span className="h-2.5 w-1/2 rounded-full bg-line animate-pulse" />
    </div>
  );
}

/** The one thing no website says, asked while the lookup reads (ADR 0049). What is tapped is the owner's. */
function WantMore({ data }: { data: ProfileResponse }) {
  const qc = useQueryClient();
  const works = data.profile.sections.find((s) => s.key === "works" && s.source === "you")?.body ?? "";
  const [picked, setPicked] = useState<string[]>(() => works ? works.split(", ").filter((w) => WANT_MORE.includes(w)) : []);
  const save = useMutation({ mutationFn: (chips: string[]) => api.tellUs("works", chips.join(", ")), onSuccess: (r) => qc.setQueryData(["profile"], r) });
  const toggle = (chip: string) => {
    const next = picked.includes(chip) ? picked.filter((c) => c !== chip) : [...picked, chip];
    setPicked(next);
    save.mutate(next);
  };
  return (
    <Panel title="While we read: what do you want more of?" aside="the one thing no website says">
      <div className="flex flex-wrap gap-2">{WANT_MORE.map((chip) => <Chip key={chip} on={picked.includes(chip)} onClick={() => toggle(chip)}>{chip}</Chip>)}</div>
      {save.error ? <p className="text-bad text-xs">{save.error.message}</p> : null}
    </Panel>
  );
}

function Look({ data }: { data: ProfileResponse }) {
  const qc = useQueryClient();
  const choose = useMutation({
    mutationFn: (choice: BrandChoice) => api.chooseBrand(choice),
    onSuccess: (r) => { qc.setQueryData(["profile"], r); void qc.invalidateQueries({ queryKey: ["media"] }); },
  });
  const { name } = nameAndTown(data.profile.sections.find((x) => x.key === "name")?.body ?? "");
  return (
    <Screen title="Your look" sub="The logo and colours every post and video is made with.">
      <LookPanel look={data.look ?? NO_LOOK} brand={data.brand ?? null} name={name} onChoose={(c) => choose.mutate(c)} choosing={choose.isPending} chooseError={choose.error?.message} />
    </Screen>
  );
}

/** Website & analytics: one line on the site, counted from then on (ADR 0035). The address is also where SEO publishes. */
function Site({ website, onMake }: { website: string | null; onMake: () => void }) {
  const [online, setOnline] = useState(false);
  const will = ["Who came, and from where: Google, Instagram, a link", "Which pages they read", "Who picked up the phone or asked for directions", "What to make next, from what worked"];
  return (
    <Screen title="Website & analytics" sub="One line on your site, and Analytics shows who visits and what they came for.">
      <Columns
        left={<Connections />}
        right={
          <>
            <Panel title="What you will see on Analytics">
              <ul className="flex flex-col">{will.map((w) => <li key={w} className="flex items-center gap-2 py-1.5 border-b border-line last:border-0 text-[12.5px]"><Icon name="analytics" size={14} />{w}</li>)}</ul>
              <p className="text-[11.5px] text-muted">No cookie and no consent banner: a visit is counted, nobody is followed.</p>
            </Panel>
            {website ? null : (
              <Panel title="No website yet?">
                <p className="text-[12.5px] leading-relaxed">Joogoos can make you one: a page about your business, written from what you told us, hosted free in your own Cloudflare account and counted here from its first visit.</p>
                <button onClick={onMake} className="self-start px-3.5 py-2 rounded-lg bg-coral text-white text-[13px] font-semibold">Make me a website ›</button>
                <p className="text-[12px] text-muted">Or skip this step: posts, channels and Customers work without one; SEO waits until there is a site to write for.</p>
                <button onClick={() => setOnline(!online)} className="self-start text-[12px] text-coral font-semibold">{online ? "Hide" : "Show me the free ways to get one"}</button>
                {online ? <OnlineTools /> : null}
              </Panel>
            )}
          </>
        }
      />
    </Screen>
  );
}

/**
 * Customers, as a step of setting up: the mailbox letters leave from, where to look, and a first draft of who to write
 * to. The same rows Customers → Set up edits; the judge, the letter and the loop stay there.
 */
function Customers({ setup, keep }: { setup: SetupResponse | undefined; keep: (d: SetupResponse) => void }) {
  const draft = useMutation({ mutationFn: () => api.draftCustomersSetup(), onSuccess: keep });
  return (
    <Screen title="Customers" sub="Joogoos finds businesses worth writing to and writes to them from your own address. Nothing is sent until you say so.">
      <Columns wide="even"
        left={<MailboxPanel />}
        right={setup ? (
          <>
            <Panel title="Where to look" aside="from your page"><Markets d={setup} keep={keep} /></Panel>
            <Panel title="Who to write to" aside={setup.groups.length ? `${setup.groups.length} ${setup.groups.length === 1 ? "group" : "groups"}` : undefined}>
              {setup.groups.length ? (
                <ul className="flex flex-col">
                  {setup.groups.map((g) => (
                    <li key={g.id} className="py-1.5 border-b border-line last:border-0 flex flex-col">
                      <span className="text-[13px] font-semibold">{g.name}</span>
                      <span className="text-[11.5px] text-muted">{g.looksFor}</span>
                    </li>
                  ))}
                </ul>
              ) : <FirstDraft draft={() => draft.mutate()} busy={draft.isPending} error={draft.error} mayEdit={setup.mayEdit} priceUsd={setup.draftUsd} />}
              <p className="text-[11.5px] text-muted">What makes one worth a letter, what the letter says and the loop are on Customers, under Set up.</p>
            </Panel>
          </>
        ) : <p className="text-sm text-faint">…</p>}
      />
    </Screen>
  );
}

/** SEO, as a step of setting up — only with a website (ADR 0049 as amended). The same rows SEO → Your website edits. */
function Seo({ seo, website, onChanged, onSite }: { seo: SeoResponse | undefined; website: string | null; onChanged: () => void; onSite: () => void }) {
  if (!website) {
    return (
      <Screen title="SEO" sub="Articles on your own website, written from sources you approve.">
        <Panel title="This waits for a website">
          <p className="text-[12.5px] leading-relaxed">Articles are published on your own site, so there is nothing to set up until there is one. Add it under Website &amp; analytics and this step opens.</p>
          <button onClick={onSite} className="self-start px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium">Website &amp; analytics ›</button>
        </Panel>
      </Screen>
    );
  }
  return (
    <Screen title="SEO" sub="Articles on your own website, written from sources you approve.">
      {seo ? (
        <Columns
          left={<WriterSettings seo={seo} onDone={onChanged} />}
          right={seo.site ? (
            <>
              <Panel title="Where they are published">
                <p className="text-[13px]">{seo.site.baseUrl.replace(/^https?:\/\//, "")}</p>
                <p className="text-[11.5px] text-muted">Publishing and your sources are on the SEO tab, under Your website.</p>
              </Panel>
              {/* Connecting and disconnecting belong here, beside the mailbox and the channels; the numbers belong on the
                  SEO tab, where they are read, and it points back here. */}
              {seo.google?.connected && seo.google.property ? (
                <Panel title="What Google says" aside="connected">
                  <p className="text-[12.5px] leading-relaxed">Search Console is connected for {seo.google.property}. What people searched, and what to write next, are on the SEO tab.</p>
                  <Disconnect onChanged={onChanged} account={seo.google.account} />
                </Panel>
              ) : <GoogleSays seo={seo} onChanged={onChanged} />}
            </>
          ) : <ConnectSite address={seo.address} suggested={seo.suggested} onDone={onChanged} onBusiness={onSite} />}
        />
      ) : <p className="text-sm text-faint">…</p>}
    </Screen>
  );
}

/**
 * One line of the check. Which way it asks depends on what the lookup could do: a line it read is Right or Edit; a
 * line it could only guess, or could not write at all, comes with answers to tap when it has some; the tone is picked
 * from three voices while it is not the owner's yet.
 */
function CheckLine({ spec, section, suggest, onSaved }: {
  spec: ProfileSectionSpec;
  section: ProfileSection | undefined;
  suggest: Suggestions | undefined;
  onSaved: (r: ProfileResponse) => void;
}) {
  const options = spec.key === "what" || spec.key === "who" || spec.key === "works" ? suggest?.[spec.key] ?? [] : [];
  const voices = spec.key === "tone" ? suggest?.voices ?? [] : [];
  const mine = section?.source === "you";
  if (!mine && voices.length) return <VoicePick spec={spec} voices={voices} section={section} onSaved={onSaved} />;
  if (!mine && options.length && (!section || section.source === "guessed")) {
    return <ChipPick spec={spec} options={options} section={section} onSaved={onSaved} />;
  }
  return <Line spec={spec} section={section} onSaved={onSaved} />;
}

/** A line of the page: its words, where they came from, Right when they are not the owner's yet, and Edit in place. */
function Line({ spec, section, onSaved }: { spec: ProfileSectionSpec; section: ProfileSection | undefined; onSaved: (r: ProfileResponse) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section?.body ?? "");
  const save = useMutation({ mutationFn: (text: string) => api.tellUs(spec.key, text), onSuccess: (r) => { onSaved(r); setEditing(false); } });
  const right = useMutation({ mutationFn: () => api.confirmLine(spec.key), onSuccess: onSaved });
  const error = save.error ?? right.error;
  return (
    <div className={`px-4 py-3 flex flex-col gap-1 ${editing ? "bg-page" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <Heading spec={spec} source={section?.source} />
          {editing ? null : section
            ? <p className="text-[13.5px] leading-relaxed whitespace-pre-line">{section.body}</p>
            : <p className="text-[13.5px] text-faint">not written yet</p>}
        </div>
        {editing ? null : (
          <div className="flex items-center gap-1.5 shrink-0">
            {section && section.source !== "you" ? (
              <button onClick={() => right.mutate()} disabled={right.isPending} className="px-2.5 py-1 rounded-md bg-ink text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40">
                <Icon name="check" size={11} /> Right
              </button>
            ) : section ? <span className="text-good text-[12px] flex items-center gap-1"><Icon name="check" size={12} /> yours</span> : null}
            <button onClick={() => { setDraft(section?.body ?? ""); setEditing(true); }} aria-label={`Change ${spec.heading}`}
              className="px-2.5 py-1 rounded-md border border-line-2 bg-panel text-[11px] font-medium">
              {section ? "Edit" : "Write it"}
            </button>
          </div>
        )}
      </div>
      {editing ? <Editor heading={spec.heading} draft={draft} setDraft={setDraft} saving={save.isPending} onSave={() => save.mutate(draft)} onCancel={() => setEditing(false)} /> : null}
      {error ? <p className="text-bad text-xs">{error.message}</p> : null}
    </div>
  );
}

/** A guess corrected with a tap: the answers `read_site` offered, any number of them, or the owner's own words. */
function ChipPick({ spec, options, section, onSaved }: { spec: ProfileSectionSpec; options: string[]; section: ProfileSection | undefined; onSaved: (r: ProfileResponse) => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const save = useMutation({ mutationFn: (text: string) => api.tellUs(spec.key, text), onSuccess: onSaved });
  const right = useMutation({ mutationFn: () => api.confirmLine(spec.key), onSuccess: onSaved });
  const toggle = (o: string) => setPicked(picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o]);
  return (
    <div className="px-4 py-3 flex flex-col gap-2">
      <Heading spec={spec} source={section?.source} hint={section ? "tap what fits" : "tap what fits, or type it"} />
      <div className="flex flex-wrap gap-2">
        {options.map((o) => <Chip key={o} on={picked.includes(o)} onClick={() => toggle(o)}>{o}</Chip>)}
        <Chip on={typing} onClick={() => setTyping(!typing)} muted>Type it</Chip>
      </div>
      {typing ? (
        <Editor heading={spec.heading} draft={draft} setDraft={setDraft} saving={save.isPending} onSave={() => save.mutate(draft)} onCancel={() => setTyping(false)} />
      ) : picked.length ? (
        <button onClick={() => save.mutate(joined(picked))} disabled={save.isPending} className="self-start px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          {save.isPending ? "Saving…" : "Save"}
        </button>
      ) : null}
      {section && !typing && !picked.length ? (
        <p className="text-[12.5px] text-muted leading-relaxed">
          {section.source === "guessed" ? "Or keep our guess" : "Or keep what we read"}: <span className="text-ink">{section.body}</span>{" "}
          <button onClick={() => right.mutate()} disabled={right.isPending} className="text-coral font-semibold">Right</button>
        </p>
      ) : null}
      {save.error ?? right.error ? <p className="text-bad text-xs">{(save.error ?? right.error)!.message}</p> : null}
    </div>
  );
}

/**
 * *How we sound*, picked from the same post written three ways. People recognise their tone and cannot describe it,
 * so the section is written from the one they pick: how it writes, and the post itself as the example.
 */
function VoicePick({ spec, voices, section, onSaved }: { spec: ProfileSectionSpec; voices: Voice[]; section: ProfileSection | undefined; onSaved: (r: ProfileResponse) => void }) {
  const save = useMutation({ mutationFn: (v: Voice) => api.tellUs(spec.key, voiceText(v)), onSuccess: onSaved });
  const right = useMutation({ mutationFn: () => api.confirmLine(spec.key), onSuccess: onSaved });
  return (
    <div className="px-4 py-3 flex flex-col gap-2">
      <Heading spec={spec} source={section?.source} hint="the same post, three ways — pick the one that sounds like you" />
      <div className="grid gap-2 sm:grid-cols-3">
        {voices.map((v) => (
          <button key={v.name} onClick={() => save.mutate(v)} disabled={save.isPending}
            className="text-left rounded-[10px] border border-line bg-panel hover:border-ink/40 p-3 flex flex-col gap-1.5 disabled:opacity-60">
            <span className="text-[11px] font-bold">{v.name} <span className="font-normal text-muted">· {v.describe}</span></span>
            <span className="text-[12.5px] leading-snug">{v.sample}</span>
          </button>
        ))}
      </div>
      {section ? (
        <p className="text-[12.5px] text-muted leading-relaxed">
          {section.source === "guessed" ? "Or keep our guess" : "Or keep what we read"}: <span className="text-ink">{section.body}</span>{" "}
          <button onClick={() => right.mutate()} disabled={right.isPending} className="text-coral font-semibold">Right</button>
        </p>
      ) : null}
      {save.error ?? right.error ? <p className="text-bad text-xs">{(save.error ?? right.error)!.message}</p> : null}
    </div>
  );
}

/** The words a voice becomes on the page: how it writes, then the post as the example the Studio can hear it in. */
export function voiceText(v: Voice): string {
  return `${v.name}: ${v.describe}.\nFor example: "${v.sample}"`;
}

/**
 * Tapped answers as one line: "Boiler servicing, emergency call-outs, leaks and taps". Commas only: the chips are
 * written in the business's own language, and an English "and" before the last one put "commerces et restaurants and
 * particuliers" on a French business page (2026-09-25).
 */
export function joined(picked: readonly string[]): string {
  return picked.map((p, i) => (i === 0 ? p : p.charAt(0).toLowerCase() + p.slice(1))).join(", ");
}

/** A business set up as an idea says when it opens: one press, free, and the page stops saying it is not open yet. */
function Opened({ onSaved }: { onSaved: (r: ProfileResponse) => void }) {
  const open = useMutation({ mutationFn: () => api.setStage("open"), onSuccess: onSaved });
  return (
    <div className="flex flex-wrap items-center gap-2.5 pt-1">
      <span className="text-[12.5px] text-muted">Not open yet — still an idea.</span>
      <button onClick={() => open.mutate()} disabled={open.isPending} className="px-2.5 py-1 rounded-md border border-line-2 bg-panel text-[12px] font-medium disabled:opacity-40">
        We've opened
      </button>
      {open.error ? <span className="text-bad text-xs">{open.error.message}</span> : null}
    </div>
  );
}

function Heading({ spec, source, hint }: { spec: ProfileSectionSpec; source?: ProfileSource | undefined; hint?: string }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted flex flex-wrap items-center gap-2">
      {spec.heading}
      {source && source !== "you" ? <span className="px-1.5 py-0.5 rounded bg-tint text-coral-2 text-[9px] font-bold uppercase tracking-wide">{SOURCE[source]}</span> : null}
      {hint ? <span className="normal-case tracking-normal font-normal text-[12px]">{hint}</span> : null}
    </h2>
  );
}

function Editor({ heading, draft, setDraft, saving, onSave, onCancel }: {
  heading: string; draft: string; setDraft: (t: string) => void; saving: boolean; onSave: () => void; onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <textarea
        autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} aria-label={`Change ${heading}`}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSave(); if (e.key === "Escape") onCancel(); }}
        className="w-full resize-none border border-line-2 rounded-lg px-2.5 py-2 text-[13.5px] leading-relaxed bg-panel outline-none focus:border-ink/30 field-sizing-content"
      />
      <div className="flex items-center gap-2">
        <button onClick={onSave} disabled={saving || !draft.trim()} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page">Cancel</button>
      </div>
    </div>
  );
}

function Chip({ on, onClick, muted, children }: { on: boolean; onClick: () => void; muted?: boolean; children: React.ReactNode }) {
  return (
    <button onClick={onClick} aria-pressed={on}
      className={`px-3.5 py-1.5 rounded-full border text-[13px] ${on ? "bg-ink border-ink text-white font-semibold" : `bg-panel border-line-2 ${muted ? "text-muted" : "text-ink"}`}`}>
      {children}
    </button>
  );
}

/**
 * The run, while it runs (03 §business context). Each step with the one line it found, because "found you — 3 answers
 * filled in" is worth more than any bar: the old client learned that the hard way with a single static label over a
 * minute of work (08).
 */
function RunSteps({ analysis }: { analysis: AnalysisState }) {
  const at = ANALYSIS_STEPS.findIndex((s) => s.key === analysis.step);
  const busy = analysis.status === "running" || analysis.status === "waiting";
  return (
    <div className="flex flex-col gap-2">
      {ANALYSIS_STEPS.map((spec, i) => {
        const found = analysis.findings[spec.key];
        const running = busy && (at < 0 ? i === 0 : i === at);
        // A choice nobody was asked for is not a step anybody needs to see.
        if (spec.gate && !found && !running) return null;
        // A step that had nothing to do is not a step that failed. Five grey lines is what a plumber with no
        // website would otherwise be shown, and it reads as a broken product rather than an honest one.
        if (found?.outcome === "skipped") return null;
        return (
          <div key={spec.key} className="flex items-baseline gap-2.5 text-[13.5px]">
            <span className="w-3.5 shrink-0 text-center">
              {found ? (found.outcome === "ok" ? <span className="text-good">✓</span> : <span className="text-faint">·</span>)
                : running ? <span className="inline-block w-2 h-2 rounded-full bg-coral animate-pulse" />
                : <span className="text-faint">·</span>}
            </span>
            <span className="min-w-0">
              <span className={found || running ? "" : "text-faint"}>{spec.label}</span>
              {found ? <span className="block text-[12px] text-muted leading-snug">{found.detail}</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Where to be online (03 §where to be online): a few places to make one page, for a business the lookup found nowhere.
 *
 * A place somebody paid for is labelled where it sits and its link says so, because a paid recommendation that
 * reads like the free ones beside it is an advert pretending not to be.
 */
export function OnlineTools({ tools = ONLINE_TOOLS }: { tools?: readonly OnlineTool[] }) {
  const kinds = (Object.keys(ONLINE_KINDS) as Array<OnlineTool["kind"]>).filter((k) => tools.some((t) => t.kind === k));
  return (
    <div className="text-[13px] leading-relaxed flex flex-col gap-2.5">
      <p>{ONLINE_ADVICE}</p>
      {kinds.map((kind) => (
        <div key={kind}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{ONLINE_KINDS[kind]}</h3>
          <ul className="mt-1 flex flex-col gap-1">
            {tools.filter((t) => t.kind === kind).map((t) => (
              <li key={t.name}>
                <a href={t.url} target="_blank" rel={t.sponsored ? "sponsored noopener noreferrer" : "noopener noreferrer"} className="font-semibold underline underline-offset-2">
                  {t.name}
                </a>
                {t.sponsored ? <span className="ml-1.5 px-1.5 py-0.5 rounded bg-tint text-coral-2 text-[9px] font-bold uppercase tracking-wide">Sponsored</span> : null}
                <span className="text-muted"> — {t.what}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
